import { DocumentTaxonomyExtraction } from "@common/types/financialDocument";

interface DocWithExtraction {
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
}

// Suffix-matches the doc's extracted account number against candidate numbers.
// Candidates subsumed by a longer candidate (e.g. mask "5678" when full number
// "99995678" is present) are pruned so the more specific number takes precedence.
// Returns true (skip) when candidates or doc number is empty.
export function doesDocumentAccountNumberMatch(
    doc: DocWithExtraction,
    expectedAccountNumberCandidates: readonly string[],
): boolean {
    const actual = normalizeAccountNumber(
        doc.taxonomyExtraction?.accountNumber,
    );

    if (!actual) {
        return true;
    }

    const normalized = expectedAccountNumberCandidates
        .map(normalizeAccountNumber)
        .filter((c): c is string => c !== null);

    if (normalized.length === 0) {
        return true;
    }

    const pruned = removeSubsumedCandidates(normalized);

    return pruned.some(
        (candidate) => candidate.endsWith(actual) || actual.endsWith(candidate),
    );
}

// Remove candidates whose digits are a suffix of a longer candidate —
// the longer one is strictly more specific.
function removeSubsumedCandidates(candidates: string[]): string[] {
    return candidates.filter(
        (c) =>
            !candidates.some(
                (other) => other.length > c.length && other.endsWith(c),
            ),
    );
}

export function normalizeAccountNumber(
    value: string | null | undefined,
): string | null {
    if (!value) {
        return null;
    }
    const digits = value.replace(/\D/g, "");
    return digits.length > 0 ? digits : null;
}
